import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';

import TkForm from '@/../bower_components/teknisavuecomponents/components/TkForm.vue';
import Field from '@/../bower_components/teknisavuecomponents/assets/interfaces/Field';
import type { SolicitacaoAprovacao } from '@/assets/ts/controller/SolicitacaoController';
import { SolicitacaoController, TiposSolicitacao } from '@/assets/ts/controller/SolicitacaoController';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import Messages from '@/assets/ts/utils/Messages';
import NovoDocumentoSolicitacaoPopup from '../documentos/NovoDocumentoSolicitacaoPopup/NovoDocumentoSolicitacaoPopup.vue';
import ToolbarAction from '@tekvue/assets/interfaces/ToolbarAction';
import Action from '@tekvue/assets/interfaces/Action';
import EditableTableColumn from '@tekvue/assets/interfaces/EditableTableColumn';
import Util from '@tekvue/assets/utils/Util';
import EditableTable from '@tekvue/components/grids/EditableTable.vue';
import FileUtils from '@tekvue/assets/utils/FileUtils';

interface AprovacaoSolicitacaoMode {
  name: string,
  save: (row: SolicitacaoAprovacao) => Promise<boolean> | boolean,
  condition: (row: SolicitacaoAprovacao) => boolean,
  labels?: { [name: string]: string },
  default?: boolean;
}

@Component({
  name: 'AprovacaoSolicitacao',
  components: {
    TkForm,
    EditableTable,
    NovoDocumentoSolicitacaoPopup
  },
})
class AprovacaoSolicitacao extends Vue {
  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly value: boolean;

  @Prop({ default: () => {} })
  private readonly solicitacao: SolicitacaoAprovacao;
  // #endregion

  // #region [ STORE ]
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-save')
  private emitAfterSave() {}

  @Emit('close')
  private emitClose() {}

  @Emit('input')
  private emitInput(value: boolean): boolean {
    return value;
  }
  // #endregion

  // #region [ MUTATIONS ]
  // Mutations
  @Mutation('setSolicitacao')
  private setSolicitacao: any;

  // #region [ DATA ]
  internalValue = false;
  private rowDefaultValue: SolicitacaoAprovacao = {
    NRSOLICITACAO: null,
    NRTIPOSOLICITACAO: null,
    NRVINCULOM: null,
    NMVINCULOM: null,
    NMFASEAPROVSOLI: null,
    NMTIPOSOLICITACAO: null,
    CDTIPOREPROVACAO: "A",
  };
  row: SolicitacaoAprovacao = { ...this.rowDefaultValue }
  loading = false;
  private extraRowFields = ['CDSTATUS'];

  rowsDocumentos: any[] = [];
  readonly defaultMainPanelsExpansion = [0];
  mainPanelsExpansion = [];

  private modes: AprovacaoSolicitacaoMode[] = [
    {
      name: 'aprovacao',
      labels: {
        save: 'Aprovar',
        title: 'Aprovação de solicitação',
        finalStepTitle: 'Aprovação de solicitação [Etapa final]',
      },
      save: this.aprovaSolicitacao,
      condition: (solicitacao: SolicitacaoAprovacao) => solicitacao.IDAPROVACAO === 'A',
      default: true,
    },
    {
      name: 'reprovacao',
      labels: {
        save: 'Reprovar',
        title: 'Reprovação de solicitação',
        finalStepTitle: 'Reprovação de solicitação [Etapa final]',
      },
      save: this.reprovaSolicitacao,
      condition: (solicitacao: SolicitacaoAprovacao) => solicitacao.IDAPROVACAO === 'R',
    },
    {
      name: 'cancelamento',
      labels: {
        save: 'Confirmar',
        title: 'Cancelar Solicitação',
        finalStepTitle: 'Cancelar Solicitação',
      },
      save: this.cancelaSolicitacao,
      condition: (solicitacao: SolicitacaoAprovacao) => solicitacao.IDAPROVACAO === 'C',
    },
  ];

  get fields(): Field[] {
    const fields: Field[] = [
      {
        name: 'NMVINCULOM',
        label: 'Vínculo',
        type: 'text',
        size: 12,
        required: false,
        readonly: true,
      },
      {
        name: 'NMTIPOSOLICITACAO',
        label: 'Tipo de solicitação',
        type: 'text',
        size: 6,
        required: false,
        readonly: true,
      },
      {
        name: 'NMFASEAPROVSOLI',
        label: 'Fase atual',
        type: 'text',
        size: 6,
        required: false,
        readonly: true,
      },
      {
        name: 'CDSTATUS',
        label: 'Status do processo',
        size: 12,
        readonly: false,
        type: 'autocomplete',
        valueField: 'CDSTATUS',
        descriptionField: 'DSSTATUS',
        route: 'getStatusProcesso',
        reloadOnCreation: true,
        filters: this.genericFilter,
        required: true,
        isVisible: this.isSolicitacaoProcessoOnLastStep
      },
      {
        name: 'CDTIPOREPROVACAO',
        label: 'Como deseja reprovar?',
        type: 'radio',
        required: true,
        valueField: 'CDTIPOREPROVACAO',
        outData: {
          DSTIPOREPROVACAO: 'label',
        },
        items: [
          {
            label: 'Voltar para a fase anterior',
            value: 'A',
          },
          {
            label: 'Voltar para o solicitante',
            value: 'I',
          },
        ],
        size: 6,
        isVisible: this.showTipoReprovacao
      },
      {
        name: 'DSOBSERVACAO',
        label: 'Observação',
        type: 'textarea',
        rows: 2,
        size: 12,
        required: true,
      },
    ];

    return fields;
  }

  documentosGrid = {
    showPopup: false,
    currentRow: null,
    toolbarActions: [
      {
        name: 'addDoc',
        label: 'Adicionar',
        icon: 'mdi-plus-circle',
        onClick: this.addDocumento,
      },
    ] as ToolbarAction[],
    actions: [
      {
        name: 'popupEdit',
        label: 'Editar',
        icon: 'mdi-pencil',
        mode: 'view',
        onClick: this.editDocumento,
      },
      'delete',
    ] as (Action | string)[],
    columns: [
      {
        value: 'NMANEXO',
        text: 'Nome',
        type: 'text',
      },
    ] as EditableTableColumn[],
  };

  get currentMode() {
    let actualMode: AprovacaoSolicitacaoMode = this.modes.find((mode) => mode.default);
    this.modes.map((mode) => actualMode = mode.condition(this.solicitacao) ? mode : actualMode);
    return actualMode;
  }

  private get isSolicitacaoProcesso() {
    return this.solicitacao.NRTIPOSOLICITACAO === TiposSolicitacao.TP_SOLI_PESSOAL;
  }

  get isSolicitacaoOnLastStep() {
    return !this.solicitacao.ID_FASE_SEGUINTE_EXISTS || this.solicitacao.ID_FASE_SEGUINTE_EXISTS === 'N';
  }

  private get isSolicitacaoProcessoOnLastStep() {
    return this.isSolicitacaoProcesso && this.isSolicitacaoOnLastStep;
  }

  get isAnexoRequired() {
    return this.solicitacao.IDANEXODOCUMENTO === 'S' && this.row.IDAPROVACAO == 'A';
  }

  get showAnexo() {
    return this.solicitacao.IDANEXODOCUMENTO === 'S';
  }

  get showTipoReprovacao() {
    return this.row.IDAPROVACAO === 'R' && this.row.IDFASEOBRIGATORIA == 'S' ;
  }

  private get genericFilter() {
    const filter = {
      NRORG: this.$store.getters.user.nrorg,
    };

    return filter;
  }

  private get requestRow(): SolicitacaoAprovacao {
    return this.buildExtraRowMapped();
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    this.internalValue = this.value;
    this.rowsDocumentos = [];

    this.row = { ...this.solicitacao, CDTIPOREPROVACAO: "A", };

    if (this.value) {
      this.mainPanelsExpansion = [...this.defaultMainPanelsExpansion];
    }
  }
  // #endregion

  // #region [ METHODS ]
  closePopup() {
    this.internalValue = false;

    this.clearRow();
    this.emitInput(this.internalValue);
  }

  private closePopupOnSuccess() {
    this.closePopup();
    this.emitClose();
  }

  async save() {
    if (!(this.row as any).validate()) {
      SnackbarUtils.showMessage(Messages.REQUIRED_FIELDS_NOT_FILLED, SnackbarUtils.TYPE_ALERT);
      return;
    }

    this.loading = true;

    try {

      const rowCopy = Util.deepCopy(this.requestRow);

      const fileValid = await this.setFile(rowCopy.EXTRA_ROW);
      if(!fileValid) return;

      const files = rowCopy.EXTRA_ROW && rowCopy.EXTRA_ROW.FILES;
      const existFiles = files && files.length;

      if (this.isAnexoRequired && !existFiles) {
        SnackbarUtils.showMessage(Messages.DOCUMENT_FILE_REQUIRED, SnackbarUtils.TYPE_ALERT);
        return;
      }

      const saveSuccess = await this.currentMode.save(rowCopy);

      if (saveSuccess) {
        this.closePopupOnSuccess();
      }
    } finally {
      this.loading = false;
    }
  }

  private async aprovaSolicitacao(row: SolicitacaoAprovacao): Promise<boolean> {
    try {
      const response = await SolicitacaoController.aprovaSolicitacao(row);
      if (response.data.messages) {
        SnackbarUtils.showMessage(response.data.messages[0].message, SnackbarUtils.TYPE_ALERT);
        return;
      }

      if (!response.data.dataset.data[0]) {
        SnackbarUtils.showMessage(Messages.SOLI_APROV_ERROR, SnackbarUtils.TYPE_ERROR);
        return false;
      }

      SnackbarUtils.showMessage(Messages.SOLI_APROV_SUCCESS, SnackbarUtils.TYPE_SUCCESS);

      const isAdmissao = row.NRTIPOSOLICITACAO === TiposSolicitacao.TP_SOLI_AUMENTO_QUADRO || row.NRTIPOSOLICITACAO === TiposSolicitacao.TP_SOLI_SUBSTITUICAO;
      const isFaseFinalAprovada = response.data.dataset.data[0].IDAPROVACAOSEC === 'A';

      if(isAdmissao && isFaseFinalAprovada) {
        this.handleSolicitacaoAdmissao(row.NRSOLICITACAO, row.NRTIPOSOLICITACAO, row.NRVINCULOM);
      }

      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_APROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  private async reprovaSolicitacao(row: SolicitacaoAprovacao): Promise<boolean> {
    try {
      const response = await SolicitacaoController.reprovaSolicitacao(row);
      if (response.data.messages) {
        SnackbarUtils.showMessage(response.data.messages[0].message, SnackbarUtils.TYPE_ALERT);
        return;
      }

      if (!response.data.dataset.data[0]) {
        SnackbarUtils.showMessage(Messages.SOLI_REPROV_ERROR, SnackbarUtils.TYPE_ERROR);
        return false;
      }

      SnackbarUtils.showMessage(Messages.SOLI_APROV_SUCCESS, SnackbarUtils.TYPE_SUCCESS);
      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_REPROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }
  
  private async cancelaSolicitacao(row: SolicitacaoAprovacao): Promise<boolean> {
    try {
      const response = await SolicitacaoController.cancelaSolicitacaoDesligamento(row);


      if (!response.data.dataset.data) {
        SnackbarUtils.showMessage(Messages.SOLI_CANCEL_ERROR, SnackbarUtils.TYPE_ERROR);
        return false;
      }

      SnackbarUtils.showMessage(Messages.SOLI_CANCEL_SUCCESS, SnackbarUtils.TYPE_SUCCESS);
      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_CANCEL_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  

  private clearRow() {
    this.row = { ...this.rowDefaultValue };
  }

  private buildExtraRowMapped(): SolicitacaoAprovacao {
    const extraRow = {};
    const rowCopy: SolicitacaoAprovacao = { ...this.row };

    this.extraRowFields.map((column) => {
      extraRow[column] = rowCopy[column];
      delete rowCopy[column];
    });

    const row: SolicitacaoAprovacao = { ...rowCopy, EXTRA_ROW: { ...extraRow } };
    return row;
  }

  private handleSolicitacaoAdmissao(nrSolicitacao: string, nrTipoSolicitacao: string, nrVinculom: string) {    
    const solicitacao = { 
      NRSOLICITACAO: nrSolicitacao,
      NRTIPOSOLICITACAO: nrTipoSolicitacao,
      NRVINCULOM: nrVinculom,
    };

    this.setSolicitacao(solicitacao);
    this.$router.push('admissao');
  }


  private addDocumento() {
    this.documentosGrid.currentRow = null;
    this.documentosGrid.showPopup = true;
  }

  private editDocumento(row: any) {
    this.documentosGrid.currentRow = row;
    this.documentosGrid.showPopup = true;
  }

  private closeDocumentoPopup() {
    this.documentosGrid.showPopup = false;
  }

  saveDocumento(row: any) {
    if (this.documentosGrid.currentRow !== null) {
      // Substitui a row antiga pela editada
      Util.replaceItem(row, this.rowsDocumentos, (doc: any) => {
        return doc.__id === row.__id;
      });
    } else {
      this.rowsDocumentos.push(row);
    }

    this.closeDocumentoPopup();
  }

  private async setFile(row: any) {
    const files = [];

    for (const docs of this.rowsDocumentos) {
      const file: File = docs.FILE;
      const fileName = file.name;
      try {
        const base64 = await FileUtils.toBase64(file);

        files.push({
          DSFILE: base64,
          NMFILE: fileName
        });
      } catch (error) {
        const message = `Não foi possível carregar o arquivo "${fileName}"!`;
        SnackbarUtils.showMessage(message, 'alert', 5000);
        return false;
      }
    }

    row.FILES = files;
    return true;
  }
  // #endregion
}

export default AprovacaoSolicitacao;
